import { HackleUser, IdentifiersBuilder, IdentifierType, Properties, User } from "../../../core/internal/model/model"
import { isSameUser, UpdateUserPropertiesOptions, UserManager } from "../../../core/internal/user/UserManager"
import ObjectUtil from "../../../core/internal/util/ObjectUtil"
import { PropertyOperations, PropertyOperationsBuilder } from "../../property/PropertyOperations"
import HacklePropertyGenerator from "../../property/HacklePropertyGenerator"
import { Clock } from "../../../core/internal/util/TimeUtil"
import { PageManager } from "../../../core/internal/page/PageManager"
import { Updated } from "../../../core/internal/util/Updated"
import { UserStorage } from "./UserStorage"
import { RemoteUserContext } from "./RemoteUserContext"
import WorkspaceEvaluationManager from "../../../core/internal/workspace/evaluation/WorkspaceEvaluationManager"
import { RemoteEvaluateContext } from "../../../core/internal/workspace/evaluation/model/RemoteEvaluateContext"

/** A pending workspace-evaluation sync: the user context to evaluate plus any property operations. */
interface SyncContext {
  userContext: RemoteUserContext
  operations: PropertyOperations
}

/**
 * Remote-mode user manager: refreshes the per-user workspace evaluation on user changes.
 *
 * Mirrors the local manager's contract but (1) syncs the server-resolved evaluation instead of
 * cohorts/target events, (2) carries property changes to the server as `operations` rather than
 * applying them locally, (3) resolves a HackleUser without cohorts/target events (the server
 * evaluates them), and (4) does not merge the campaign parsed from the current URL into the
 * resolved properties. Mutation methods are fused — they apply the change and sync when needed.
 *
 * On (4): the local manager merges the parsed campaign under the user's own properties, so the
 * locally persisted campaign takes precedence. `RemoteUserContext` strips properties, so here the
 * merge would be the only source of user properties. It would also break two things. The campaign is
 * recorded on session start but the parse reads the page on every resolve, so an SPA route change to
 * different utm params would move the evaluated properties with no change to stored state. And
 * `resetUser()` would send `$clearAll` together with `userProperties: { utm_source: ... }`.
 *
 * Known cost: events resolved here carry no campaign properties. `CampaignManager` and
 * `SessionEventTracker` are session listeners registered in that order, so the campaign operations
 * and `$session_start` are produced in the same sweep but leave on different endpoints — the event
 * on a 500ms flush timer, the campaign on an evaluate round trip. A first-touch `$session_start` can
 * be ingested before the campaign is stored.
 */
export class RemoteUserManager extends UserManager {
  private context: RemoteUserContext
  // Pending first sync (prefetch): the initial user context + its properties as operations. Consumed once by sync().
  private initSyncContext: SyncContext | null = null

  constructor(
    private readonly hackleDeviceId: string,
    private readonly storage: UserStorage,
    private readonly pageManager: PageManager,
    private readonly clock: Clock,
    previousUser: User | null,
    initUser: User | null,
    private readonly evaluationManager: WorkspaceEvaluationManager
  ) {
    super()
    this.context = RemoteUserContext.from(this.defaultUser)
    this.initialize(initUser ?? previousUser ?? null)
  }

  private get defaultUser(): User {
    return { id: this.hackleDeviceId, deviceId: this.hackleDeviceId }
  }

  initialize(user?: User | null): void {
    const initUser = decorate(user ?? this.defaultUser, this.hackleDeviceId)
    this.context = RemoteUserContext.from(initUser)
    this.storage.saveUser(this.context.user)
    this.initSyncContext = { userContext: this.context, operations: setOps(initUser.properties) }
  }

  get currentUser(): User {
    return this.context.user
  }

  // HackleUser resolve (pure — no mutation)

  hackleUser(user?: User | string): HackleUser {
    const resolved: User = user === undefined ? this.context.user : typeof user === "string" ? { id: user } : user
    return this.buildHackleUser(resolved)
  }

  private buildHackleUser(user: User): HackleUser {
    const builder = new IdentifiersBuilder()
      .addIdentifiers(user.identifiers || {})
      .add(IdentifierType.ID, user.id || this.hackleDeviceId)
      .add(IdentifierType.DEVICE, user.deviceId || this.hackleDeviceId)
      .add(IdentifierType.HACKLE_DEVICE, this.hackleDeviceId)

    if (ObjectUtil.isNotNullOrUndefined(user.userId)) {
      builder.add(IdentifierType.USER, user.userId)
    }
    const identifiers = builder.build()

    // No campaign merge here — see item (4) on the class doc.
    const userProperties: Properties = user.properties ?? {}

    // No cohorts/target events — the server evaluates them for remote mode.
    return {
      identifiers: identifiers,
      properties: userProperties,
      hackleProperties: this.hackleProperties()
    }
  }

  private hackleProperties(): Properties {
    if (typeof window !== "undefined") {
      return HacklePropertyGenerator.generate(window, this.pageManager.currentPage)
    }
    return {}
  }

  // User update (fused: apply the change, then sync the workspace evaluation if needed)

  setUser(user: User): Promise<void> {
    return this.updateAndSyncIfNeeded(setOps(user.properties), () =>
      RemoteUserContext.from(decorate(user, this.hackleDeviceId))
    )
  }

  setUserId(userId: string | undefined): Promise<void> {
    return this.updateAndSyncIfNeeded(new PropertyOperations(), (context) =>
      RemoteUserContext.from({ ...context.user, userId })
    )
  }

  setDeviceId(deviceId: string): Promise<void> {
    return this.updateAndSyncIfNeeded(new PropertyOperations(), (context) =>
      RemoteUserContext.from({ ...context.user, deviceId })
    )
  }

  resetUser(): Promise<void> {
    return this.updateAndSyncIfNeeded(clearAllOps(), () => RemoteUserContext.from(this.defaultUser))
  }

  updateUserProperties(operations: PropertyOperations, options?: UpdateUserPropertiesOptions): Promise<void> {
    const merged = options?.serverOnlyOperations ? operations.merge(options.serverOnlyOperations) : operations
    if (isEmpty(merged)) {
      return Promise.resolve()
    }
    return this.syncOf({ userContext: this.context, operations: merged })
  }

  private updateAndSyncIfNeeded(
    operations: PropertyOperations,
    update: (context: RemoteUserContext) => RemoteUserContext
  ): Promise<void> {
    const updated = this.updateContext(update)
    return this.syncIfNeeded(updated, operations)
  }

  private updateContext(update: (context: RemoteUserContext) => RemoteUserContext): Updated<RemoteUserContext> {
    const oldContext = this.context
    const newContext = update(oldContext)
    this.context = newContext

    if (!isSameUser(oldContext.user, newContext.user)) {
      this.notifyUserUpdated(oldContext.user, newContext.user, this.clock.currentMillis())
      this.storage.saveUser(newContext.user)
    }

    return new Updated<RemoteUserContext>(oldContext, newContext)
  }

  // Syncs when there are pending operations or when the evaluation key (identifiers) changed.
  private syncIfNeeded(updated: Updated<RemoteUserContext>, operations: PropertyOperations): Promise<void> {
    if (!isEmpty(operations)) {
      return this.syncOf({ userContext: updated.current, operations })
    }
    if (updated.previous.evaluationKey() !== updated.current.evaluationKey()) {
      return this.syncOf({ userContext: updated.current, operations })
    }
    return Promise.resolve()
  }

  private syncOf(context: SyncContext): Promise<void> {
    return this.evaluationManager.sync(
      RemoteEvaluateContext.of(this.buildHackleUser(context.userContext.user), context.operations)
    )
  }

  // Synchronizer: onReady prefetch + fetch() re-sync. The first call consumes the init prefetch.
  sync(): Promise<void> {
    const context = this.initSyncContext ?? { userContext: this.context, operations: new PropertyOperations() }
    this.initSyncContext = null
    return this.syncOf(context)
  }

  async close(): Promise<void> {}
}

const decorate = (user: User, hackleDeviceId: string): User => {
  return {
    ...user,
    id: user.id || hackleDeviceId,
    deviceId: user.deviceId || hackleDeviceId
  }
}

const setOps = (properties?: Properties): PropertyOperations => {
  const builder = new PropertyOperationsBuilder()
  if (properties) {
    for (const [key, value] of Object.entries(properties)) {
      builder.set(key, value)
    }
  }
  return builder.build()
}

const clearAllOps = (): PropertyOperations => {
  return new PropertyOperationsBuilder().clearAll().build()
}

const isEmpty = (operations: PropertyOperations): boolean => {
  return Object.keys(operations.toRecord()).length === 0
}
